import React from "react";
import { useDocument } from "../loan_tool";
import CurrencyField from "../../fields/currency_field";

// ── Helpers ────────────────────────────────────────────────────────────────────

function num(val) {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
}

function sumFields(data, ...keys) {
  return keys.reduce((acc, k) => acc + num(data[k]), 0);
}

function formatDollars(n) {
  return "$" + n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

const DEFAULT_PAGE2 = {
  // Kitchen main (cabinets, electrical, counter tops, misc entered directly)
  k_cabinets: "0", k_flooring: "0", k_electrical: "0",
  k_counter_tops: "0", k_miscellaneous: "0",
  // Appliances sub-section
  app_stove: "0", app_oven: "0", app_microwave: "0", app_refrigerator: "0",
  app_dishwasher: "0", app_warming_drawer: "0", app_trash_compactor: "0",
  app_cook_top: "0", app_wet_bar_fridge: "0", app_kegerator: "0", app_misc: "0", app_misc2: "0",
  // Plumbing sub-section
  plumb_disposal: "0", plumb_faucets: "0", plumb_water_devices: "0", plumb_misc: "0", plumb_misc2: "0",
  // Lighting sub-section
  light_can_lights: "0", light_under_cabinet: "0", light_misc: "0", light_misc2: "0",
  // Misc items sub-section
  misc_water_heater: "0", misc_copper_pipes: "0", misc_items: "0", misc_items2: "0",
  // Master Bedroom
  mbed_flooring: "0", mbed_closet_doors: "0", mbed_electrical: "0",
  mbed_windows: "0", mbed_misc: "0",
  // Bedroom 1
  bed1_flooring: "0", bed1_closet_doors: "0", bed1_electrical: "0",
  bed1_windows: "0", bed1_misc: "0",
  // Bedroom 2
  bed2_flooring: "0", bed2_closet_doors: "0", bed2_electrical: "0",
  bed2_windows: "0", bed2_misc: "0",
  // Bedroom 3
  bed3_flooring: "0", bed3_closet_doors: "0", bed3_electrical: "0",
  bed3_windows: "0", bed3_misc: "0",
  // Bedroom 4 / Office
  bed4_flooring: "0", bed4_closet_doors: "0", bed4_electrical: "0",
  bed4_windows: "0", bed4_misc: "0",
  // Master Bathroom
  mbath_flooring: "0", mbath_closet_doors: "0", mbath_electrical: "0",
  mbath_windows: "0", mbath_misc: "0",
  // Bathroom 1
  bath1_flooring: "0", bath1_closet_doors: "0", bath1_electrical: "0",
  bath1_windows: "0", bath1_misc: "0",
  // Bathroom 2
  bath2_flooring: "0", bath2_closet_doors: "0", bath2_electrical: "0",
  bath2_windows: "0", bath2_misc: "0",
  // Bathroom 3
  bath3_flooring: "0", bath3_closet_doors: "0", bath3_electrical: "0",
  bath3_windows: "0", bath3_misc: "0",
  // Living Room & Entry
  living_flooring: "0", living_paint: "0", living_fireplace: "0", living_misc: "0",
  // Dining Room
  dining_flooring: "0", dining_doors: "0", dining_windows: "0",
  dining_lighting: "0", dining_misc: "0"
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function TotalRow({ label, value }) {
  return (
    <div className="ff-total-row">
      <span className="ff-total-label">{label}</span>
      <span className="ff-total-value">{formatDollars(value)}</span>
    </div>
  );
}

function RoomSection({ title, prefix, fields, data, onChange }) {
  const total = sumFields(data, ...fields.map(f => prefix + "_" + f.key));
  return (
    <div className="ff-room-box">
      <div className="ff-room-title">{title}</div>
      {fields.map(f => (
        <CurrencyField
          key={f.key}
          name={f.label}
          fieldName={prefix + "_" + f.key}
          fixedDigits={0}
          value={data[prefix + "_" + f.key] || "0"}
          onChange={onChange}
        />
      ))}
      <TotalRow label="Total" value={total} />
    </div>
  );
}

const BED_FIELDS = [
  { key: "flooring", label: "Flooring" },
  { key: "closet_doors", label: "Closet Doors" },
  { key: "electrical", label: "Electrical" },
  { key: "windows", label: "Windows" },
  { key: "misc", label: "Miscellaneous" }
];

const BATH_FIELDS = [
  { key: "flooring", label: "Flooring" },
  { key: "closet_doors", label: "Tile / Fixtures" },
  { key: "electrical", label: "Electrical" },
  { key: "windows", label: "Windows" },
  { key: "misc", label: "Miscellaneous" }
];

// ── Main component ─────────────────────────────────────────────────────────────

export default function FixAndFlipPage2() {
  const [document, setDocument, { lastSaveDate }, { saveDocument }] = useDocument();

  const fix_and_flip = document.fix_and_flip || {};
  const page2 = { ...DEFAULT_PAGE2, ...fix_and_flip.page2 };

  function updatePage2(updatedFields) {
    setDocument({
      ...document,
      fix_and_flip: {
        ...fix_and_flip,
        page2: { ...page2, ...updatedFields }
      }
    });
  }

  // ── Kitchen calculations ───────────────────────────────────────────────────
  const appTotal = sumFields(page2,
    "app_stove", "app_oven", "app_microwave", "app_refrigerator",
    "app_dishwasher", "app_warming_drawer", "app_trash_compactor",
    "app_cook_top", "app_wet_bar_fridge", "app_kegerator", "app_misc", "app_misc2"
  );
  const plumbTotal = sumFields(page2,
    "plumb_disposal", "plumb_faucets", "plumb_water_devices", "plumb_misc", "plumb_misc2"
  );
  const lightTotal = sumFields(page2,
    "light_can_lights", "light_under_cabinet", "light_misc", "light_misc2"
  );
  const miscItemsTotal = sumFields(page2,
    "misc_water_heater", "misc_copper_pipes", "misc_items", "misc_items2"
  );

  const kitchenTotal =
    num(page2.k_cabinets) +
    num(page2.k_flooring) +
    appTotal +
    plumbTotal +
    lightTotal +
    num(page2.k_electrical) +
    num(page2.k_counter_tops) +
    miscItemsTotal +
    num(page2.k_miscellaneous);

  // ── Room totals ────────────────────────────────────────────────────────────
  const mBedTotal = sumFields(page2, "mbed_flooring", "mbed_closet_doors", "mbed_electrical", "mbed_windows", "mbed_misc");
  const bed1Total = sumFields(page2, "bed1_flooring", "bed1_closet_doors", "bed1_electrical", "bed1_windows", "bed1_misc");
  const bed2Total = sumFields(page2, "bed2_flooring", "bed2_closet_doors", "bed2_electrical", "bed2_windows", "bed2_misc");
  const bed3Total = sumFields(page2, "bed3_flooring", "bed3_closet_doors", "bed3_electrical", "bed3_windows", "bed3_misc");
  const bed4Total = sumFields(page2, "bed4_flooring", "bed4_closet_doors", "bed4_electrical", "bed4_windows", "bed4_misc");
  const mBathTotal = sumFields(page2, "mbath_flooring", "mbath_closet_doors", "mbath_electrical", "mbath_windows", "mbath_misc");
  const bath1Total = sumFields(page2, "bath1_flooring", "bath1_closet_doors", "bath1_electrical", "bath1_windows", "bath1_misc");
  const bath2Total = sumFields(page2, "bath2_flooring", "bath2_closet_doors", "bath2_electrical", "bath2_windows", "bath2_misc");
  const bath3Total = sumFields(page2, "bath3_flooring", "bath3_closet_doors", "bath3_electrical", "bath3_windows", "bath3_misc");
  const livingTotal = sumFields(page2, "living_flooring", "living_paint", "living_fireplace", "living_misc");
  const diningTotal = sumFields(page2, "dining_flooring", "dining_doors", "dining_windows", "dining_lighting", "dining_misc");

  const col1Subtotal =
    kitchenTotal + mBedTotal + bed1Total + bed2Total + bed3Total + bed4Total +
    mBathTotal + bath1Total + bath2Total + bath3Total + livingTotal + diningTotal;

  function formatLastSaved() {
    if (!lastSaveDate) return "Not yet saved";
    return "Last saved: " + new Date(lastSaveDate).toLocaleTimeString();
  }

  return (
    <div>
      {/* Kitchen Section */}
      <div className="ff-section">
        <div className="ff-section-title">Kitchen Repairs &amp; Upgrades</div>
        <div className="ff-kitchen-layout">
          {/* Left: Kitchen main line items */}
          <div className="ff-kitchen-main">
            <div className="ff-subsection-title">Kitchen — Main Items</div>
            <CurrencyField name="Cabinets" fieldName="k_cabinets" fixedDigits={0} value={page2.k_cabinets} onChange={updatePage2} />
            <CurrencyField name="Flooring" fieldName="k_flooring" fixedDigits={0} value={page2.k_flooring} onChange={updatePage2} />
            <TotalRow label="Appliances (see detail →)" value={appTotal} />
            <TotalRow label="Plumbing (see detail →)" value={plumbTotal} />
            <TotalRow label="Lighting (see detail →)" value={lightTotal} />
            <CurrencyField name="Electrical" fieldName="k_electrical" fixedDigits={0} value={page2.k_electrical} onChange={updatePage2} />
            <CurrencyField name="Counter Tops" fieldName="k_counter_tops" fixedDigits={0} value={page2.k_counter_tops} onChange={updatePage2} />
            <TotalRow label="Misc Items (see detail →)" value={miscItemsTotal} />
            <CurrencyField name="Miscellaneous" fieldName="k_miscellaneous" fixedDigits={0} value={page2.k_miscellaneous} onChange={updatePage2} />
            <div className="ff-section-total">
              <span>Kitchen Total</span>
              <span>{formatDollars(kitchenTotal)}</span>
            </div>
          </div>

          {/* Right: Detail sub-sections */}
          <div className="ff-kitchen-detail">
            {/* Appliances */}
            <div className="ff-room-box">
              <div className="ff-room-title ff-sub-title">Appliances Detail</div>
              {[
                { key: "app_stove", label: "Stove" },
                { key: "app_oven", label: "Oven" },
                { key: "app_microwave", label: "Microwave" },
                { key: "app_refrigerator", label: "Refrigerator" },
                { key: "app_dishwasher", label: "Dishwasher" },
                { key: "app_warming_drawer", label: "Warming Drawer" },
                { key: "app_trash_compactor", label: "Trash Compactor" },
                { key: "app_cook_top", label: "Cook Top" },
                { key: "app_wet_bar_fridge", label: "Wet Bar Fridge" },
                { key: "app_kegerator", label: "Kegerator" },
                { key: "app_misc", label: "Miscellaneous" },
                { key: "app_misc2", label: "Miscellaneous 2" }
              ].map(f => (
                <CurrencyField
                  key={f.key}
                  name={f.label}
                  fieldName={f.key}
                  fixedDigits={0}
                  value={page2[f.key]}
                  onChange={updatePage2}
                />
              ))}
              <TotalRow label="Appliances Sub-Total" value={appTotal} />
            </div>

            {/* Plumbing */}
            <div className="ff-room-box">
              <div className="ff-room-title ff-sub-title">Plumbing Detail</div>
              {[
                { key: "plumb_disposal", label: "Garbage Disposal" },
                { key: "plumb_faucets", label: "Faucets" },
                { key: "plumb_water_devices", label: "Water Devices" },
                { key: "plumb_misc", label: "Miscellaneous" },
                { key: "plumb_misc2", label: "Miscellaneous 2" }
              ].map(f => (
                <CurrencyField
                  key={f.key}
                  name={f.label}
                  fieldName={f.key}
                  fixedDigits={0}
                  value={page2[f.key]}
                  onChange={updatePage2}
                />
              ))}
              <TotalRow label="Plumbing Sub-Total" value={plumbTotal} />
            </div>

            {/* Lighting */}
            <div className="ff-room-box">
              <div className="ff-room-title ff-sub-title">Lighting Detail</div>
              {[
                { key: "light_can_lights", label: "Recessed / Can Lights" },
                { key: "light_under_cabinet", label: "Under Cabinet" },
                { key: "light_misc", label: "Miscellaneous" },
                { key: "light_misc2", label: "Miscellaneous 2" }
              ].map(f => (
                <CurrencyField
                  key={f.key}
                  name={f.label}
                  fieldName={f.key}
                  fixedDigits={0}
                  value={page2[f.key]}
                  onChange={updatePage2}
                />
              ))}
              <TotalRow label="Lighting Sub-Total" value={lightTotal} />
            </div>

            {/* Misc Items */}
            <div className="ff-room-box">
              <div className="ff-room-title ff-sub-title">Misc Items Detail</div>
              {[
                { key: "misc_water_heater", label: "Water Heater" },
                { key: "misc_copper_pipes", label: "Copper Pipes" },
                { key: "misc_items", label: "Miscellaneous" },
                { key: "misc_items2", label: "Miscellaneous 2" }
              ].map(f => (
                <CurrencyField
                  key={f.key}
                  name={f.label}
                  fieldName={f.key}
                  fixedDigits={0}
                  value={page2[f.key]}
                  onChange={updatePage2}
                />
              ))}
              <TotalRow label="Misc Items Sub-Total" value={miscItemsTotal} />
            </div>
          </div>
        </div>
      </div>

      {/* Bedrooms & Bathrooms */}
      <div className="ff-section">
        <div className="ff-section-title">Bedrooms, Bathrooms &amp; Common Areas</div>
        <div className="ff-rooms-layout">
          {/* Left column */}
          <div className="ff-rooms-col">
            <RoomSection title="Master Bedroom" prefix="mbed" fields={BED_FIELDS} data={page2} onChange={updatePage2} />
            <RoomSection title="Bedroom #1" prefix="bed1" fields={BED_FIELDS} data={page2} onChange={updatePage2} />
            <RoomSection title="Bedroom #2" prefix="bed2" fields={BED_FIELDS} data={page2} onChange={updatePage2} />
            <RoomSection title="Bedroom #3" prefix="bed3" fields={BED_FIELDS} data={page2} onChange={updatePage2} />
            <RoomSection title="Bedroom #4 / Office" prefix="bed4" fields={BED_FIELDS} data={page2} onChange={updatePage2} />
          </div>

          {/* Right column */}
          <div className="ff-rooms-col">
            <RoomSection title="Master Bathroom" prefix="mbath" fields={BATH_FIELDS} data={page2} onChange={updatePage2} />
            <RoomSection title="Bathroom #1" prefix="bath1" fields={BATH_FIELDS} data={page2} onChange={updatePage2} />
            <RoomSection title="Bathroom #2" prefix="bath2" fields={BATH_FIELDS} data={page2} onChange={updatePage2} />
            <RoomSection title="Bathroom #3" prefix="bath3" fields={BATH_FIELDS} data={page2} onChange={updatePage2} />

            {/* Living Room & Entry */}
            <div className="ff-room-box">
              <div className="ff-room-title">Living Room &amp; Entry</div>
              <CurrencyField name="Flooring" fieldName="living_flooring" fixedDigits={0} value={page2.living_flooring} onChange={updatePage2} />
              <CurrencyField name="Paint" fieldName="living_paint" fixedDigits={0} value={page2.living_paint} onChange={updatePage2} />
              <CurrencyField name="Fireplace Redo" fieldName="living_fireplace" fixedDigits={0} value={page2.living_fireplace} onChange={updatePage2} />
              <CurrencyField name="Miscellaneous" fieldName="living_misc" fixedDigits={0} value={page2.living_misc} onChange={updatePage2} />
              <TotalRow label="Total" value={livingTotal} />
            </div>

            {/* Dining Room */}
            <div className="ff-room-box">
              <div className="ff-room-title">Dining Room</div>
              <CurrencyField name="Flooring" fieldName="dining_flooring" fixedDigits={0} value={page2.dining_flooring} onChange={updatePage2} />
              <CurrencyField name="Doors" fieldName="dining_doors" fixedDigits={0} value={page2.dining_doors} onChange={updatePage2} />
              <CurrencyField name="Windows" fieldName="dining_windows" fixedDigits={0} value={page2.dining_windows} onChange={updatePage2} />
              <CurrencyField name="Lighting" fieldName="dining_lighting" fixedDigits={0} value={page2.dining_lighting} onChange={updatePage2} />
              <CurrencyField name="Miscellaneous" fieldName="dining_misc" fixedDigits={0} value={page2.dining_misc} onChange={updatePage2} />
              <TotalRow label="Total" value={diningTotal} />
            </div>
          </div>
        </div>
      </div>

      {/* Column 1 Subtotal Banner */}
      <div className="ff-col1-subtotal">
        <span>Column 1 Subtotal (Rooms &amp; Kitchen)</span>
        <span className="ff-col1-subtotal-value">{formatDollars(col1Subtotal)}</span>
      </div>

      {/* Footer */}
      <div className="ff-footer">
        <span className="ff-last-saved">{formatLastSaved()}</span>
        <div className="ff-footer-actions">
          <button className="button" onClick={saveDocument}>
            Save to Loan File
          </button>
        </div>
      </div>
    </div>
  );
}
